// src/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  CardMembership as PackageIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [userTeams, setUserTeams] = useState([]);

  // User profile data
  const [profile, setProfile] = useState({
    id: '',
    email: '',
    username: '',
    full_name: '',
    company_name: '',
    team_name: '',
    package_name: '',
    role: '',
    created_at: '',
    email_confirmed: false,
    auth_provider: 'Email',
  });

  // Editable fields
  const [editableProfile, setEditableProfile] = useState({
    username: '',
    full_name: '',
  });

  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Quota information
  const [quotaInfo, setQuotaInfo] = useState({
    used_files: 0,
    limit_files: 0,
    used_storage_mb: 0,
    limit_storage_mb: 0,
    used_sessions: 0,
    limit_sessions: 0,
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError('');

    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, email, created_at, company_id, onboarding_complete')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;

      // Fetch company name separately if company_id exists
      let companyName = 'No Company';
      if (profileData.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('name')
          .eq('company_id', profileData.company_id)
          .single();
        
        if (companyError) {
          console.error('Error fetching company:', companyError);
        }
        
        if (companyData) {
          companyName = companyData.name;
        }
      }

      // Fetch team name from team_members join
      let teamName = 'No Team';
      let teamId = null;
      let userRole = 'user';
      
      // Get all team memberships for this user
      const { data: allTeamMemberData } = await supabase
        .from('team_members')
        .select('team_id, role_id')
        .eq('user_id', user.id);
      
      const teamsArray = [];
      
      if (allTeamMemberData && allTeamMemberData.length > 0) {
        // Get first team as primary
        teamId = allTeamMemberData[0].team_id;
        
        if (allTeamMemberData[0].role_id === 1) {
          userRole = 'admin';
        } else if (allTeamMemberData[0].role_id === 2) {
          userRole = 'member';
        }
        
        // Fetch details for all teams
        for (const tm of allTeamMemberData) {
          const { data: teamData } = await supabase
            .from('teams')
            .select('team_id, name, created_at')
            .eq('team_id', tm.team_id)
            .single();
          
          if (teamData) {
            // Get member count for this team
            const { count: memberCount, error: memberError } = await supabase
              .from('team_members')
              .select('*', { count: 'exact', head: true })
              .eq('team_id', tm.team_id);
            
            if (memberError) {
              console.error('Error fetching member count:', memberError);
            }
            
            // Get file count for this team
            const { count: fileCount, error: fileError } = await supabase
              .from('user_files')
              .select('*', { count: 'exact', head: true })
              .eq('team_id', tm.team_id)
              .neq('status', 'deleted');
            
            if (fileError) {
              console.error('Error fetching file count:', fileError);
            }
            
            teamsArray.push({
              team_id: teamData.team_id,
              name: teamData.name,
              created_at: teamData.created_at,
              role: tm.role_id === 1 ? 'admin' : 'member',
              member_count: memberCount || 0,
              file_count: fileCount || 0,
              is_primary: tm.team_id === teamId,
            });
          }
        }
        
        // Set primary team name
        if (teamsArray.length > 0) {
          teamName = teamsArray[0].name;
        }
      }
      
      setUserTeams(teamsArray);

      // Fetch company subscription and quota
      let packageName = 'Free Plan';
      let quotaData = {
        used_files: 0,
        limit_files: 0,
        used_storage_mb: 0,
        limit_storage_mb: 0,
        used_sessions: 0,
        limit_sessions: 0,
      };

      if (profileData.company_id) {
        // Fetch subscription with limits
        const { data: subscriptionData } = await supabase
          .from('company_subscriptions')
          .select('package_id, status, limit_total_files, limit_total_upload_mb, limit_monthly_sessions')
          .eq('company_id', profileData.company_id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (subscriptionData) {
          // Fetch package name
          const { data: packageData } = await supabase
            .from('packages')
            .select('name')
            .eq('package_id', subscriptionData.package_id)
            .single();
          
          if (packageData) {
            packageName = packageData.name;
          }

          // Fetch current month quota
          const currentMonth = new Date().toISOString().slice(0, 7) + '-01';
          const { data: quotaDataResult, error: quotaError } = await supabase
            .from('company_quotas')
            .select('used_total_files, used_total_upload, used_monthly_sessions')
            .eq('company_id', profileData.company_id)
            .eq('usage_period_start', currentMonth)
            .single();

          if (quotaError) {
            console.error('Error fetching quota:', quotaError);
          }

          if (quotaDataResult) {
            quotaData = {
              used_files: quotaDataResult.used_total_files || 0,
              limit_files: subscriptionData.limit_total_files || 0,
              // used_total_upload is already in MB in the database
              used_storage_mb: quotaDataResult.used_total_upload || 0,
              limit_storage_mb: subscriptionData.limit_total_upload_mb || 0,
              used_sessions: quotaDataResult.used_monthly_sessions || 0,
              limit_sessions: subscriptionData.limit_monthly_sessions || 0,
            };
          } else {
            // If no quota record exists for current month, set limits from subscription
            quotaData = {
              used_files: 0,
              limit_files: subscriptionData.limit_total_files || 0,
              used_storage_mb: 0,
              limit_storage_mb: subscriptionData.limit_total_upload_mb || 0,
              used_sessions: 0,
              limit_sessions: subscriptionData.limit_monthly_sessions || 0,
            };
          }
        }
      }

      // Check if user signed up with OAuth (Google, etc.)
      const provider = user.app_metadata?.provider || 'email';
      const isOAuth = provider !== 'email';
      setIsOAuthUser(isOAuth);
      
      // Get provider display name (capitalize first letter)
      const providerDisplayName = provider.charAt(0).toUpperCase() + provider.slice(1);

      setProfile({
        id: user.id,
        email: user.email || profileData.email || '',
        username: user.email?.split('@')[0] || '',  // Use email prefix as username
        full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
        company_name: companyName,
        team_name: teamName,
        package_name: packageName,
        role: userRole,
        created_at: profileData.created_at || user.created_at || '',
        email_confirmed: user.email_confirmed_at ? true : false,
        auth_provider: providerDisplayName,
      });

      setEditableProfile({
        username: user.email?.split('@')[0] || '',
        full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
      });

      setQuotaInfo(quotaData);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(`Failed to load profile: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit - reset to original values
      setEditableProfile({
        username: profile.username,
        full_name: profile.full_name,
      });
    }
    setEditMode(!editMode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');

    try {
      // Update user metadata in auth.users table
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: editableProfile.full_name,
          name: editableProfile.full_name,  // Also set 'name' for compatibility
        }
      });

      if (updateError) throw updateError;

      setProfile((prev) => ({
        ...prev,
        username: editableProfile.username,
        full_name: editableProfile.full_name,
      }));

      setSuccessMessage('Profile updated successfully!');
      setOpenSnackbar(true);
      setEditMode(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(`Failed to update profile: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setError('');

    // Validate passwords
    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setSaving(true);

    try {
      // Supabase automatically encrypts passwords using bcrypt
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) throw updateError;

      setSuccessMessage('Password changed successfully!');
      setOpenSnackbar(true);
      setChangePasswordOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      console.error('Error changing password:', err);
      setError(`Failed to change password: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete the team "${teamName}"? This action cannot be undone.`)) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Delete team - this will cascade delete team_members due to foreign key constraints
      const { error: deleteError } = await supabase
        .from('teams')
        .delete()
        .eq('team_id', teamId);

      if (deleteError) throw deleteError;

      setSuccessMessage(`Team "${teamName}" deleted successfully!`);
      setOpenSnackbar(true);
      
      // Refresh profile data
      fetchUserProfile();
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(`Failed to delete team: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === 0 || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chat')}
        sx={{ mb: 2 }}
      >
        Back to Chat
      </Button>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Header Card */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                My Profile
              </Typography>
              <Box>
                {editMode ? (
                  <>
                    <IconButton color="error" onClick={handleEditToggle} disabled={saving}>
                      <CancelIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={handleSaveProfile} disabled={saving}>
                      {saving ? <CircularProgress size={24} /> : <SaveIcon />}
                    </IconButton>
                  </>
                ) : (
                  <IconButton color="primary" onClick={handleEditToggle}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              {/* Avatar */}
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(profile.full_name || profile.username || profile.email)}
              </Avatar>

              {/* Profile Info */}
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="full_name"
                      value={editMode ? editableProfile.full_name : profile.full_name}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      value={profile.username}
                      disabled
                      helperText="Derived from your email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={profile.email}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                        endAdornment: profile.email_confirmed ? (
                          <Chip label="Verified" color="success" size="small" />
                        ) : (
                          <Chip label="Unverified" color="warning" size="small" />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Role"
                      value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Member Since"
                      value={formatDate(profile.created_at)}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Organization Info */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Organization
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.company_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Team
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" />
                    {profile.team_name}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Info */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PackageIcon color="primary" />
                Subscription
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Plan
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    <Chip label={profile.package_name} color="primary" />
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/upgrade')}
                  sx={{ mt: 1 }}
                >
                  Upgrade Plan
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Statistics */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Usage Statistics
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                {/* Files */}
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Files Uploaded
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {quotaInfo.used_files} / {quotaInfo.limit_files === -1 ? '∞' : quotaInfo.limit_files}
                    </Typography>
                    <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${getUsagePercentage(quotaInfo.used_files, quotaInfo.limit_files)}%`,
                          height: '100%',
                          bgcolor: `${getUsageColor(getUsagePercentage(quotaInfo.used_files, quotaInfo.limit_files))}.main`,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Storage */}
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Storage Used
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {quotaInfo.used_storage_mb.toFixed(1)} MB / {quotaInfo.limit_storage_mb === -1 ? '∞' : `${quotaInfo.limit_storage_mb} MB`}
                    </Typography>
                    <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${getUsagePercentage(quotaInfo.used_storage_mb, quotaInfo.limit_storage_mb)}%`,
                          height: '100%',
                          bgcolor: `${getUsageColor(getUsagePercentage(quotaInfo.used_storage_mb, quotaInfo.limit_storage_mb))}.main`,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Sessions */}
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sessions This Month
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {quotaInfo.used_sessions} / {quotaInfo.limit_sessions === -1 ? '∞' : quotaInfo.limit_sessions}
                    </Typography>
                    <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${getUsagePercentage(quotaInfo.used_sessions, quotaInfo.limit_sessions)}%`,
                          height: '100%',
                          bgcolor: `${getUsageColor(getUsagePercentage(quotaInfo.used_sessions, quotaInfo.limit_sessions))}.main`,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Teams */}
        {userTeams.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="primary" />
                  My Teams
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {userTeams.map((team) => (
                    <Box
                      key={team.team_id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {team.name}
                          </Typography>
                          {team.is_primary && (
                            <Chip label="Primary" size="small" color="primary" />
                          )}
                          <Chip label={team.role} size="small" variant="outlined" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {team.member_count} {team.member_count === 1 ? 'member' : 'members'} • {team.file_count} {team.file_count === 1 ? 'file' : 'files'}
                        </Typography>
                      </Box>
                      {team.file_count === 0 && team.member_count <= 1 && team.role === 'admin' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteTeam(team.team_id, team.name)}
                          disabled={saving}
                        >
                          Delete Team
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Security Settings */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Security
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {isOAuthUser ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You signed in with {profile.auth_provider}. Password management is handled by your {profile.auth_provider} account.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    To change your password, please visit your {profile.auth_provider} account settings.
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setChangePasswordOpen(true)}
                >
                  Change Password
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
            />
            <Typography variant="caption" color="text.secondary">
              Password must be at least 6 characters long.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={saving}>
            {saving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;

